import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/site-shell";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ArrowRight } from "lucide-react";
import { DISCIPLINE_BY_SLUG } from "@/lib/disciplines";
import { PreviewPanel } from "./index";

export const Route = createFileRoute("/for/$slug")({
  loader: ({ params }) => {
    const discipline = DISCIPLINE_BY_SLUG.get(params.slug) ?? null;
    return { discipline };
  },
  head: ({ loaderData }) => {
    const discipline = loaderData?.discipline;
    if (!discipline) {
      return {
        meta: [
          { title: "Page not found — DealDraft" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const title = `${discipline.headline} — DealDraft`;
    return {
      meta: [
        { title },
        { name: "description", content: discipline.subheadline },
        { property: "og:title", content: title },
        { property: "og:description", content: discipline.subheadline },
        { property: "og:type", content: "website" },
        { property: "og:site_name", content: "DealDraft" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: discipline.subheadline },
        {
          "script:ld+json": {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: discipline.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        },
      ],
      links: [{ rel: "canonical", href: `https://dealdraft.pro/for/${discipline.slug}` }],
    };
  },
  component: DisciplinePage,
});

function DisciplinePage() {
  const { discipline } = Route.useLoaderData();

  if (!discipline) {
    return (
      <SiteShell>
        <div className="mx-auto max-w-lg px-6 py-20 text-center">
          <h1 className="text-xl font-semibold">Page not found</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            We don't have a page for that discipline yet.{" "}
            <Link to="/generate" className="underline hover:no-underline">
              Try the generator directly →
            </Link>
          </p>
        </div>
      </SiteShell>
    );
  }

  const singularLabel = discipline.label.replace(/s$/, "");

  return (
    <SiteShell>
      <section className="mx-auto max-w-3xl px-6 pt-16 pb-12 text-center">
        <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">{discipline.headline}</h1>
        <p className="mx-auto mt-5 max-w-2xl text-base text-muted-foreground sm:text-lg">
          {discipline.subheadline}
        </p>
        <div className="mt-8">
          <Button asChild size="lg" className="h-12 rounded-lg px-7 text-base">
            <Link to="/generate" search={{ type: discipline.slug }}>
              Generate Your First Proposal Free <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>

      <section className="bg-secondary/40 py-16">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-center text-2xl font-semibold tracking-tight sm:text-3xl">
            Everything You Need to Send a {singularLabel} Proposal
          </h2>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {discipline.features.map((f) => (
              <div key={f.title} className="rounded-xl border border-border bg-card p-6 shadow-sm">
                <div className="text-2xl">{f.emoji}</div>
                <h3 className="mt-3 text-base font-semibold">{f.title}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-2xl px-6 py-16 text-center">
        <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
          Why {discipline.label} Need This
        </h2>
        <p className="mt-4 text-base text-muted-foreground">{discipline.whyParagraph}</p>
      </section>

      <section className="mx-auto max-w-2xl px-6 py-16">
        <h2 className="text-center text-2xl font-semibold tracking-tight sm:text-3xl">
          What a {singularLabel} Proposal Looks Like
        </h2>
        <div className="mt-10">
          <PreviewPanel
            badge={`${singularLabel} Proposal`}
            badgeClass="bg-primary/10 text-primary"
            lines={[discipline.sampleScope, "", discipline.sampleDeliverables]}
          />
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-6 py-16">
        <h2 className="text-center text-2xl font-semibold tracking-tight sm:text-3xl">
          Common Questions from {discipline.label}
        </h2>
        <Accordion type="single" collapsible className="mt-10">
          {discipline.faqs.map((f) => (
            <AccordionItem key={f.q} value={f.q}>
              <AccordionTrigger className="text-left text-base font-medium">{f.q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <section className="mx-auto max-w-3xl px-6 pb-16">
        <h2 className="text-center text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Related Proposal Templates
        </h2>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
          {discipline.relatedSlugs.map((slug) => {
            const related = DISCIPLINE_BY_SLUG.get(slug);
            if (!related) return null;
            return (
              <Link
                key={slug}
                to="/for/$slug"
                params={{ slug }}
                className="rounded-full border border-border px-4 py-2 text-sm font-medium hover:border-foreground hover:text-foreground"
              >
                {related.label}
              </Link>
            );
          })}
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-6 pb-20 text-center">
        <div className="rounded-2xl bg-gradient-to-br from-primary to-[oklch(0.466_0.207_268)] p-10 text-primary-foreground sm:p-14">
          <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">
            Your next proposal is 60 seconds away.
          </h2>
          <p className="mt-3 text-primary-foreground/80">
            Free to start. No credit card. No excuses.
          </p>
          <div className="mt-7">
            <Button
              asChild
              size="lg"
              variant="secondary"
              className="h-12 rounded-lg px-7 text-base text-primary"
            >
              <Link to="/generate" search={{ type: discipline.slug }}>
                Generate My Proposal Now →
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
